import { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  Copy,
  Link2,
  MapPin,
  Smartphone,
  Trash2,
  X,
} from "lucide-react";

const DEMO_TIMING = {
  highlightMs: 1400,
  openHoldMs: 7200,
  closedHoldMs: 1600,
  animMs: 220,
  scrollPauseTopMs: 800,
  scrollDurationMs: 4200,
};

const LINKS = [
  {
    id: "summer",
    campaign: "Summer Affiliate Drop",
    actualLink: "brandstore.com/summer-sale",
    generatedLink: "infuber.com/a/c4f92b1a",
    created: "05/03/2026",
    visits: 186,
    uniqueVisits: 142,
    conversions: 11,
    cr: "5.9%",
    status: "24 days left",
    statusTone: "active",
    showCopy: true,
  },
  // {
  //   id: "skincare",
  //   campaign: "Skincare Glow Kit",
  //   actualLink: "glowlab.co/kit",
  //   generatedLink: "infuber.com/a/7e21d04c",
  //   created: "12/03/2026",
  //   visits: 94,
  //   uniqueVisits: 71,
  //   conversions: 4,
  //   cr: "4.3%",
  //   status: "18 days left",
  //   statusTone: "active",
  //   showCopy: true,
  // },
  {
    id: "hanuman",
    campaign: "Hanuman Tekri Launch",
    actualLink: "hanumantekri.app/offer",
    generatedLink: "infuber.com/a/aba891e8",
    created: "18/02/2026",
    visits: 128,
    uniqueVisits: 97,
    conversions: 3,
    cr: "2.3%",
    status: "Expired",
    statusTone: "expired",
    showDelete: true,
  },
];

const INSIGHTS = {
  campaign: "Summer Affiliate Drop",
  devices: [
    { label: "iPhone / Safari", share: "48%", visits: 68 },
    { label: "Android / Chrome", share: "31%", visits: 44 },
    { label: "Desktop / Chrome", share: "15%", visits: 21 },
    { label: "iPad / Safari", share: "6%", visits: 9 },
  ],
  locations: [
    { label: "Mumbai, India", share: "34%", visits: 48 },
    { label: "Delhi, India", share: "22%", visits: 31 },
    { label: "Bengaluru, India", share: "18%", visits: 26 },
    { label: "Pune, India", share: "14%", visits: 20 },
    { label: "Other cities", share: "12%", visits: 17 },
  ],
};

/**
 * Marketing mockup only — compact affiliate links UI with auto product-tour.
 * No API calls.
 */
const AffiliateShowcaseMockup = () => {
  const rootRef = useRef(null);
  const insightsBodyRef = useRef(null);
  const timersRef = useRef([]);
  const rafRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | highlight | open
  const [panelVisible, setPanelVisible] = useState(false);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const schedule = useCallback((fn, ms) => {
    const id = window.setTimeout(fn, ms);
    timersRef.current.push(id);
  }, []);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) =>
        setInView(entry.isIntersecting && entry.intersectionRatio > 0.3),
      { threshold: [0, 0.3, 0.55] }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    clearTimers();
    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!inView) {
      setPhase("idle");
      setPanelVisible(false);
      return undefined;
    }

    if (reducedMotion) {
      setPhase("open");
      setPanelVisible(true);
      return undefined;
    }

    let cancelled = false;

    const runCycle = () => {
      if (cancelled) return;
      setPhase("idle");
      setPanelVisible(false);

      schedule(() => {
        if (cancelled) return;
        setPhase("highlight");

        schedule(() => {
          if (cancelled) return;
          setPhase("open");
          requestAnimationFrame(() => {
            if (!cancelled) setPanelVisible(true);
          });

          schedule(() => {
            if (cancelled) return;
            setPanelVisible(false);
            schedule(() => {
              if (cancelled) return;
              setPhase("idle");
              schedule(runCycle, DEMO_TIMING.closedHoldMs);
            }, DEMO_TIMING.animMs);
          }, DEMO_TIMING.openHoldMs);
        }, DEMO_TIMING.highlightMs);
      }, DEMO_TIMING.closedHoldMs);
    };

    runCycle();
    return () => {
      cancelled = true;
      clearTimers();
    };
  }, [inView, clearTimers, schedule]);

  useEffect(() => {
    const el = insightsBodyRef.current;
    if (!panelVisible || !el) return undefined;

    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    el.scrollTop = 0;
    if (reducedMotion) return undefined;

    let cancelled = false;
    let startTime = 0;

    const easeInOut = (t) =>
      t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

    const startScroll = () => {
      if (cancelled || !insightsBodyRef.current) return;
      const node = insightsBodyRef.current;
      const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight);
      if (maxScroll < 8) return;

      startTime = performance.now();
      const tick = (now) => {
        if (cancelled || !insightsBodyRef.current) return;
        const progress = Math.min(
          1,
          (now - startTime) / DEMO_TIMING.scrollDurationMs
        );
        insightsBodyRef.current.scrollTop = maxScroll * easeInOut(progress);
        if (progress < 1) {
          rafRef.current = window.requestAnimationFrame(tick);
        }
      };
      rafRef.current = window.requestAnimationFrame(tick);
    };

    const pauseId = window.setTimeout(startScroll, DEMO_TIMING.scrollPauseTopMs);
    return () => {
      cancelled = true;
      window.clearTimeout(pauseId);
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [panelVisible]);

  const panelOpen = phase === "open";

  // Block user scroll/interaction so only the auto-demo moves the panel
  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    const block = (e) => e.preventDefault();
    node.addEventListener("wheel", block, { passive: false });
    node.addEventListener("touchmove", block, { passive: false });
    return () => {
      node.removeEventListener("wheel", block);
      node.removeEventListener("touchmove", block);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="relative cursor-none select-none overflow-hidden rounded-2xl border border-white/10 bg-white shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)]"
    >
      {/* Header */}
      <div className="border-b border-gray-100 px-3 py-3 sm:px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-gray-900 sm:text-base">
              Your Affiliate Links
            </h3>
            <p className="mt-0.5 text-[10px] text-gray-500 sm:text-xs">
              Generate, share, and track performance in one place.
            </p>
          </div>
          <span className="shrink-0 rounded-lg bg-orange-600 px-2.5 py-1.5 text-[9px] font-semibold text-white sm:rounded-xl sm:text-[10px]">
            + Generate Link
          </span>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-1.5 sm:gap-2">
          <StatPill label="Active Links" value="2" />
          <StatPill label="Total Visits" value="408" accent />
          <StatPill label="Conversions" value="18" />
        </div>
      </div>

      {/* Compact link rows */}
      <div className="space-y-2 p-3 sm:p-4">
        {LINKS.map((link, index) => (
          <LinkCard
            key={link.id}
            {...link}
            highlightDevices={phase === "highlight" && index === 0}
          />
        ))}
      </div>

      {/* Compact generate strip */}
      <div className="border-t border-gray-100 bg-gray-50/80 px-3 py-3 sm:px-4">
        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
            <Link2 className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold text-gray-900">
              Generate Affiliate Link
            </p>
            <p className="truncate text-[9px] text-gray-500">
              Campaign → product URL → trackable short link
            </p>
          </div>
          <span className="shrink-0 rounded-lg bg-orange-600 px-2.5 py-1.5 text-[9px] font-semibold text-white">
            Generate
          </span>
        </div>
      </div>

      {/* Insights overlay — auto opens from Devices */}
      {panelOpen && (
        <div
          className={`absolute inset-0 z-20 flex items-end justify-center bg-black/45 p-2.5 transition-opacity duration-200 sm:items-center sm:p-3 ${
            panelVisible ? "opacity-100" : "opacity-0"
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="affiliate-insights-title"
        >
          <div
            className={`flex max-h-[94%] w-full max-w-sm flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl transition-all duration-200 sm:max-w-md sm:rounded-2xl ${
              panelVisible
                ? "translate-y-0 scale-100 opacity-100"
                : "translate-y-3 scale-95 opacity-0"
            }`}
          >
            <div className="flex shrink-0 items-start justify-between gap-2 border-b border-gray-100 px-3 py-2.5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Smartphone className="h-3.5 w-3.5 text-orange-600" />
                  <p
                    id="affiliate-insights-title"
                    className="text-xs font-bold text-gray-900"
                  >
                    Click Insights
                  </p>
                  <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[8px] font-semibold text-emerald-700">
                    Live
                  </span>
                </div>
                <p className="mt-0.5 truncate text-[9px] text-gray-500">
                  {INSIGHTS.campaign} · 142 unique visits
                </p>
              </div>
              <span className="rounded-md p-1 text-gray-400">
                <X className="h-3.5 w-3.5" />
              </span>
            </div>

            <div
              ref={insightsBodyRef}
              className="pointer-events-none min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-3 py-2.5"
            >
              <div className="grid grid-cols-3 gap-1.5">
                <MiniStat label="Visits" value="186" />
                <MiniStat label="Unique" value="142" />
                <MiniStat label="CR" value="5.9%" accent />
              </div>

              <div>
                <div className="flex items-center gap-1 text-[8px] font-semibold uppercase tracking-wide text-gray-500">
                  <Smartphone className="h-2.5 w-2.5 text-blue-500" />
                  Devices
                </div>
                <ul className="mt-1.5 space-y-1">
                  {INSIGHTS.devices.map((item) => (
                    <InsightRow key={item.label} {...item} barTone="blue" />
                  ))}
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-1 text-[8px] font-semibold uppercase tracking-wide text-gray-500">
                  <MapPin className="h-2.5 w-2.5 text-emerald-500" />
                  Locations
                </div>
                <ul className="mt-1.5 space-y-1">
                  {INSIGHTS.locations.map((item) => (
                    <InsightRow key={item.label} {...item} barTone="green" />
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-orange-100 bg-orange-50/50 p-2">
                <p className="text-[8px] font-semibold uppercase tracking-wide text-orange-700">
                  Quick takeaway
                </p>
                <p className="mt-1 text-[9px] leading-snug text-gray-700">
                  Most conversions come from mobile in metro cities. Share the
                  short link in Stories and Reels captions for the best CR.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const GuideCursor = () => (
  <span
    aria-hidden
    className="pointer-events-none absolute -bottom-1 -right-1 h-3 w-3 translate-x-1 translate-y-1 rounded-full border-2 border-white bg-orange-500 shadow-md"
    style={{ animation: "affiliate-cursor-tap 1.1s ease-in-out infinite" }}
  >
    <style>{`
      @keyframes affiliate-cursor-tap {
        0%, 100% { transform: translate(2px, 2px) scale(1); opacity: 1; }
        50% { transform: translate(0px, 0px) scale(0.85); opacity: 0.85; }
      }
    `}</style>
  </span>
);

const StatPill = ({ label, value, accent = false }) => (
  <div
    className={`rounded-lg border p-2 sm:rounded-xl sm:p-2.5 ${
      accent
        ? "border-orange-100 bg-orange-50/70"
        : "border-gray-100 bg-gray-50"
    }`}
  >
    <p className="truncate text-[8px] font-semibold uppercase tracking-wide text-gray-400 sm:text-[9px]">
      {label}
    </p>
    <p className="mt-0.5 text-xs font-bold text-gray-900 sm:text-sm">{value}</p>
  </div>
);

const LinkCard = ({
  campaign,
  actualLink,
  generatedLink,
  created,
  visits,
  uniqueVisits,
  conversions,
  cr,
  status,
  statusTone,
  showCopy = false,
  showDelete = false,
  highlightDevices = false,
}) => (
  <div
    className={`rounded-xl border p-2.5 shadow-sm transition-colors sm:p-3 ${
      highlightDevices
        ? "border-orange-200 bg-orange-50/40"
        : "border-gray-200 bg-white"
    }`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <p className="truncate text-[11px] font-semibold text-gray-900 sm:text-xs">
            {campaign}
          </p>
          <span
            className={`rounded-full px-1.5 py-0.5 text-[8px] font-semibold ring-1 sm:text-[9px] ${
              statusTone === "expired"
                ? "bg-rose-50 text-rose-600 ring-rose-100"
                : "bg-emerald-50 text-emerald-700 ring-emerald-100"
            }`}
          >
            {status}
          </span>
        </div>
        <p className="mt-0.5 truncate text-[9px] text-gray-500 sm:text-[10px]">
          {actualLink}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <span className="max-w-[110px] truncate text-[9px] font-medium text-orange-600 sm:max-w-[130px] sm:text-[10px]">
          {generatedLink}
        </span>
        {showCopy && (
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-gray-100 text-gray-500">
            <Copy className="h-3 w-3" />
          </span>
        )}
        {showDelete && (
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-rose-50 text-rose-500">
            <Trash2 className="h-3 w-3" />
          </span>
        )}
      </div>
    </div>

    <div className="mt-2 grid grid-cols-4 gap-1.5">
      <Metric label="Visits" value={visits} />
      <Metric label="Unique" value={uniqueVisits} />
      <Metric label="Conv." value={conversions} />
      <Metric label="CR" value={cr} highlight />
    </div>

    <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-2">
      <span className="text-[8px] text-gray-400 sm:text-[9px]">
        Created {created}
      </span>
      <span
        className={`relative inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[8px] font-medium ring-1 sm:text-[9px] ${
          highlightDevices
            ? "bg-blue-100 text-blue-700 ring-blue-200 animate-pulse"
            : "bg-gray-50 text-gray-600 ring-gray-100"
        }`}
      >
        <Smartphone className="h-2.5 w-2.5" />
        Devices ({uniqueVisits})
        {highlightDevices && <GuideCursor />}
      </span>
      <span className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-1.5 py-0.5 text-[8px] font-medium text-gray-600 ring-1 ring-gray-100 sm:text-[9px]">
        <MapPin className="h-2.5 w-2.5" />
        Locations ({uniqueVisits})
      </span>
      {statusTone === "active" && (
        <span className="ml-auto hidden items-center gap-1 text-[8px] font-medium text-emerald-600 sm:inline-flex sm:text-[9px]">
          <CheckCircle2 className="h-2.5 w-2.5" />
          Tracking live
        </span>
      )}
    </div>
  </div>
);

const Metric = ({ label, value, highlight = false }) => (
  <div className="rounded-md bg-gray-50 px-1.5 py-1.5 sm:rounded-lg sm:px-2">
    <p className="text-[7px] font-semibold uppercase tracking-wide text-gray-400 sm:text-[8px]">
      {label}
    </p>
    <p
      className={`mt-0.5 text-[11px] font-bold sm:text-xs ${
        highlight ? "text-orange-600" : "text-gray-900"
      }`}
    >
      {value}
    </p>
  </div>
);

const MiniStat = ({ label, value, accent = false }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50 px-2 py-2 text-center">
    <p className="text-[8px] font-semibold uppercase tracking-wide text-gray-400">
      {label}
    </p>
    <p
      className={`mt-0.5 text-sm font-bold ${
        accent ? "text-orange-600" : "text-gray-900"
      }`}
    >
      {value}
    </p>
  </div>
);

const InsightRow = ({ label, share, visits, barTone }) => (
  <li className="rounded-lg border border-gray-100 bg-gray-50/80 px-2 py-1.5">
    <div className="flex items-center justify-between gap-2">
      <p className="truncate text-[9px] font-medium text-gray-700">{label}</p>
      <p className="shrink-0 text-[9px] font-semibold text-gray-900">{share}</p>
    </div>
    <div className="mt-1 flex h-1 overflow-hidden rounded-full bg-gray-200">
      <div
        className={barTone === "blue" ? "bg-blue-500" : "bg-emerald-500"}
        style={{ width: share }}
      />
    </div>
    <p className="mt-0.5 text-[8px] text-gray-400">{visits} visits</p>
  </li>
);

export default AffiliateShowcaseMockup;
